import { Close, ContentCopyOutlined } from '@mui/icons-material';
import {
  Box,
  Chip,
  Drawer,
  DrawerProps,
  IconButton,
  Stack,
  SxProps,
  Tooltip,
} from '@mui/material';
import { CSSProperties, ReactNode } from 'react';
import { getColor } from '../../assets/theme';
import LoadingComponent from '../animation/loading-component';
import ContainerTitle from '@components/ui/container-title';
import CopyToClipboard from 'react-copy-to-clipboard';
import useDisclosure from '@hooks/useDisclosure';

/**
 * Interface extending DrawerProps with additional properties for the DrawerWrapper component.
 */
interface DrawerInterface extends DrawerProps {
  /** The content to be rendered inside the drawer */
  children: ReactNode;
  /** Function to be called when the drawer is closed */
  onClose: () => void;
  /** Custom styles for the close icon */
  iconStyle?: CSSProperties | any;
  /** Whether the close icon should stick to the top of the drawer */
  stickyCloseIcon?: boolean;
  /** Whether the drawer is in a loading state */
  isLoading?: boolean;
  /** Custom styles for the loading component */
  loadingComponentSx?: SxProps;
  withHeader?: {
    title: string | ReactNode;
    learnMore?: () => void;
    sx?: SxProps;
    copyToClipboard?: string;
    additionalContent?: ReactNode;
    wrapperSx?: SxProps;
  };
  allowEscToClose?: boolean;
}

/**
 * A wrapper component for Material-UI Drawer with additional functionality.
 *
 * @param {DrawerInterface} props - The props for the DrawerWrapper component
 * @returns {React.ReactElement} The rendered DrawerWrapper component
 */
const DrawerWrapper: React.FC<DrawerInterface> = ({
  children,
  onClose,
  iconStyle,
  stickyCloseIcon,
  isLoading = false,
  loadingComponentSx,
  withHeader,
  allowEscToClose,
  ...etc
}) => {
  const { primary, bgColor, white } = getColor();

  const clipBoard = useDisclosure();

  // #region POS CONFIG
  const pathname = window.location.pathname?.split('/')?.[1];
  const isPOS = pathname === 'point-of-sale';
  // #endregion

  return (
    <Drawer
      anchor='right'
      {...etc}
      sx={{
        isolation: 'isolate',
        '.MuiDrawer-paper': {
          width: { xs: '100%', sm: 'auto' },
          pointerEvents: isLoading ? 'none' : 'unset',
        },
        ...etc?.sx,
      }}
      onClose={allowEscToClose || isPOS ? onClose : undefined}
    >
      {isLoading && (
        <LoadingComponent
          wrapperStyle={{ bgcolor: bgColor, opacity: 1, ...loadingComponentSx }}
        />
      )}

      {/* CLOSE ICON */}
      {!withHeader && (
        <Box
          sx={
            stickyCloseIcon
              ? { position: 'sticky', top: 0, right: 0, zIndex: 30 }
              : {}
          }
        >
          <IconButton
            id='drawer-close-icon'
            disableRipple
            sx={[
              {
                position: 'absolute',
                top: '3px',
                right: '3px',
                border: 0,
                bgcolor: 'transparent!important',
                '&.MuiButtonBase-root': { bgcolor: 'transparent!important' },
                ':hover': {
                  bgcolor: 'transparent',
                  color: primary,
                },
                padding: '24px',
                zIndex: 9999,
              },
              iconStyle || {},
            ]}
            onClick={onClose}
          >
            <Close />
          </IconButton>
        </Box>
      )}

      {withHeader ? (
        <Stack
          flex={1}
          sx={{
            maxHeight: '100dvh',
            ...withHeader?.sx,
          }}
        >
          {/* HEADER */}
          <Stack
            direction='row'
            gap={{ xs: 1, md: 2 }}
            sx={{
              p: '22px',
              borderBottom: '1px solid #EEE',
              position: 'sticky',
              top: 0,
              bgcolor: white,
              zIndex: 21,
              pb: '18px',
              justifyContent: 'space-between',
            }}
          >
            <Stack flex={1}>
              {typeof withHeader.title === 'string' ? (
                <Stack
                  direction='row'
                  sx={{
                    gap: 1,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flex: 1,
                    ...withHeader?.wrapperSx,
                  }}
                >
                  <ContainerTitle
                    title={withHeader.title}
                    func={withHeader?.learnMore || undefined}
                    titleStyle={{ fontSize: '20px!important' }}
                    clipboard={
                      withHeader.copyToClipboard ? (
                        <Chip
                          onDelete={() => {}}
                          deleteIcon={
                            <CopyToClipboard
                              text={withHeader.copyToClipboard}
                              onCopy={() => {
                                clipBoard.onOpen();
                                setTimeout(() => {
                                  clipBoard.onClose();
                                }, 500);
                              }}
                            >
                              <Tooltip
                                placement='right'
                                arrow
                                title='Copied'
                                open={clipBoard.isOpen}
                              >
                                <ContentCopyOutlined
                                  sx={{
                                    ...(clipBoard.isOpen && {
                                      color: `${primary}!important`,
                                    }),
                                  }}
                                />
                              </Tooltip>
                            </CopyToClipboard>
                          }
                          sx={{
                            '& .MuiChip-deleteIcon': {
                              fontSize: '20px',
                            },
                            '& .MuiChip-label': { pl: '2px' },
                          }}
                        />
                      ) : (
                        <></>
                      )
                    }
                  />
                  {withHeader.additionalContent}
                </Stack>
              ) : (
                withHeader.title
              )}
            </Stack>

            <IconButton id='drawer-close-icon' disableRipple onClick={onClose}>
              <Close />
            </IconButton>
          </Stack>

          {/* MAIN */}
          <Stack
            className='main-wrapper'
            flex={1}
            sx={{
              minWidth: { xs: '100%', sm: '400px' },
              // overflowY: 'hidden',
              // ':hover': {
              //   overflowY: 'auto',
              // },
              minHeight: 0,
            }}
          >
            {children}
          </Stack>
        </Stack>
      ) : (
        children
      )}
    </Drawer>
  );
};

export default DrawerWrapper;
